import { FileTextIcon, Lightbulb, BarChart3Icon, BookOpenIcon, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import ScrollToTop from "@/components/utils/ScrollToTop";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

export default function ResearchPage() {

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="bg-white py-16 px-4 sm:px-6 lg:px-8 border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-6 text-primary-900">
              Research & White Papers
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl">
              Comprehensive analysis and evidence supporting the StaffAbility model for justice reform through employment.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Publications Grid */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            
            {/* White Paper & Impact Report 2.0 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-primary-100 rounded-lg p-2 text-primary-700 mr-3">
                    <FileTextIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">StaffAbility Model Overview</h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                      Updated
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  A comprehensive white paper outlining StaffAbility's mission to break down career barriers and provide 
                  second chances through innovative nonprofit staffing solutions for justice-involved individuals.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/1FB4e2OVsvXjox6g4-tFB8YZZledqvrp2/view?usp=sharing', '_blank')}
                >
                  Download White Paper
                </Button>
              </div>
            </div>

            {/* Business Case for Hiring */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 rounded-lg p-2 text-purple-700 mr-3">
                    <BarChart3Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Business Case for Hiring</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Comprehensive analysis of the business benefits and ROI of hiring justice-involved individuals, 
                  including retention rates, productivity metrics, and cost-benefit analysis.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/1bEk3pvt-i3HEWkL_VdKBNaVH8T3s0RY_/view?usp=sharing', '_blank')}
                >
                  Download Research Paper
                </Button>
              </div>
            </div>

            {/* Impact & Opportunity Report */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 rounded-lg p-2 text-blue-700 mr-3">
                    <BarChart3Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Impact & Opportunity Report</h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      Featured
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Detailed analysis of the economic and social impact of second-chance employment programs, 
                  featuring data-driven insights and measurable outcomes from successful implementations.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/14f0KAvvUro8OYduO08PXQy8QePHpj21k/view', '_blank')}
                >
                  Download Impact Report
                </Button>
              </div>
            </div>

            {/* Youth Crime Prevention Research */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 rounded-lg p-2 text-green-700 mr-3">
                    <Shield className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Youth Crime Prevention</h3>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                      New
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Evidence-based strategies for preventing youth crimes in the United States, examining successful 
                  intervention programs and their impact on reducing juvenile delinquency rates.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/1XrO22ggt0BQPKIsNcEVcKGfXQ6kCg_zg/view?usp=sharing', '_blank')}
                >
                  Download Research Paper
                </Button>
              </div>
            </div>

            {/* Collateral Consequences */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-red-100 rounded-lg p-2 text-red-700 mr-3">
                    <Shield className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Collateral Consequences</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  An examination of the collateral consequences of criminal convictions in the United States 
                  and their impact on reintegration, employment opportunities, and community outcomes.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/1JXPMYklsIQUlgxnop4eGR7BWkbFIGS8Q/view?usp=sharing', '_blank')}
                >
                  Download Research Paper
                </Button>
              </div>
            </div>

            {/* Vocational Training */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-indigo-100 rounded-lg p-2 text-indigo-700 mr-3">
                    <Lightbulb className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Vocational & Soft Skills Training</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Comprehensive analysis of vocational and soft skills training programs for justice-involved individuals, 
                  examining outcomes, best practices, and implementation strategies.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/17n1T2-mFSyaQAa9amQDk9YH0kj6HAo0B/view?usp=sharing', '_blank')}
                >
                  Download Research Paper
                </Button>
              </div>
            </div>

            {/* Impact of Term Felon */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-yellow-100 rounded-lg p-2 text-yellow-700 mr-3">
                    <FileTextIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Impact of "Felon" Label</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Research on the impact of the term "felon" and record-based exclusion in the United States, 
                  exploring stigma effects and barriers to successful reintegration.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/1z7DHZcJ9v11XSYR9QHFga6nmUAAKvMoR/view?usp=sharing', '_blank')}
                >
                  Download Research Paper
                </Button>
              </div>
            </div>

            {/* Hybrid Nonprofit Models */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-teal-100 rounded-lg p-2 text-teal-700 mr-3">
                    <BarChart3Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Hybrid Nonprofit Models</h3>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Analysis of hybrid nonprofit–for-profit models for second chances in the U.S., 
                  examining innovative approaches to sustainable social impact and business viability.
                </p>
                <Button 
                  className="w-full"
                  onClick={() => window.open('https://drive.google.com/file/d/1H3-R7Ayke7UJg8QjTbAZQxkBAoqT8sfH/view?usp=sharing', '_blank')}
                >
                  Download Research Paper
                </Button>
              </div>
            </div>

            {/* Request Custom Research */}
            <div className="bg-primary-50 rounded-xl border-2 border-dashed border-primary-200 overflow-hidden">
              <div className="p-6 text-center">
                <div className="bg-primary-100 rounded-lg p-3 w-fit mx-auto mb-4">
                  <BookOpenIcon className="h-8 w-8 text-primary-700" />
                </div>
                <h3 className="text-lg font-semibold text-primary-900 mb-2">Need Custom Research?</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  Our research team can provide customized analysis for your specific industry, region, or research questions.
                </p>
                <Button variant="outline" className="w-full">
                  Request Custom Research
                </Button>
              </div>
            </div>
          </div>


        </div>
        <Footer />
      </div>
    </>
  );
}